<script module lang="ts">
  export interface PillItem {
    value: string;
    label: string;
    /** Couleur d'accent quand la pill est active (étiquettes métier). */
    color?: string | null;
    /** Ligne secondaire, ex. la cadence d'un groupe. */
    hint?: string | null;
  }
</script>

<script lang="ts">
  /**
   * Grille de pills sélectionnables — le seul organe de saisie de la fiche
   * société. Pas de champ texte, pas de menu déroulant, pas de bouton
   * « Enregistrer » : un tap écrit.
   *
   * Les pills s'enroulent au lieu de défiler horizontalement, parce que
   * l'usage visé est le pouce d'une main devant la télé — un scroll
   * horizontal cache la moitié des choix et oblige à viser.
   */

  import { haptic } from "../gestures/haptic";

  interface Props {
    items: PillItem[];
    selected: string[];
    onToggle: (value: string) => void;
    /** Rend la pill active en rouge — réservé au « bruit ». */
    danger?: string;
  }

  let { items, selected, onToggle, danger }: Props = $props();

  /**
   * Encre lisible sur une étiquette colorée. Les couleurs viennent du CRM et
   * vont du vert très clair (#BCEB6A) au vert sombre (#6E8741) : forcer une
   * encre unique en rendrait la moitié illisible. Luminance perçue, seuil au
   * milieu — la formule suffit largement pour un texte de 14 px.
   */
  function inkOn(color: string): string {
    const m = /^#?([0-9a-f]{6})$/i.exec(color.trim());
    if (!m) return "#17130e";
    const n = parseInt(m[1], 16);
    const lum =
      (0.299 * ((n >> 16) & 255) +
        0.587 * ((n >> 8) & 255) +
        0.114 * (n & 255)) /
      255;
    return lum > 0.55 ? "#17130e" : "#ffffff";
  }

  function tap(value: string) {
    haptic("medium");
    onToggle(value);
  }
</script>

<div class="row">
  {#each items as item (item.value)}
    {@const on = selected.includes(item.value)}
    <button
      class="pill"
      class:on
      class:danger={on && danger === item.value}
      style={on && item.color
        ? `--pill: ${item.color}; --pill-ink: ${inkOn(item.color)}`
        : undefined}
      aria-pressed={on}
      onclick={() => tap(item.value)}
    >
      <span class="label">{item.label}</span>
      {#if item.hint}<span class="hint">{item.hint}</span>{/if}
    </button>
  {/each}
</div>

<style>
  .row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .pill {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    /* 40px de haut : la cible confortable au pouce, à l'aveugle. */
    min-height: 40px;
    padding: 9px 14px;
    border-radius: 999px;
    font-family: var(--font-sans);
    font-size: 14px;
    font-weight: 500;
    letter-spacing: -0.01em;
    background: transparent;
    color: var(--fg-muted);
    border: 1px solid var(--border);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition:
      background 0.12s,
      color 0.12s,
      border-color 0.12s;
  }
  .pill:active {
    transform: scale(0.97);
  }
  .pill.on {
    background: var(--pill, var(--fg));
    color: var(--pill-ink, var(--bg));
    border-color: var(--pill, var(--fg));
  }
  .pill.danger {
    background: var(--bad);
    border-color: var(--bad);
    color: #fff;
  }
  .hint {
    font-family: var(--font-mono);
    font-size: 10.5px;
    opacity: 0.7;
  }
</style>
